// frontend/src/pages/admin/AdminPiracyDashboard.tsx
// Полная страница мониторинга пиратоопасных районов (admin-only)
// v2.0 — двойной клик в таблицах, email-модалка, draggable dialogs, терминология

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Fade,
} from '@mui/material';
import type { PaperProps } from '@mui/material/Paper';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import PiracyMap from '../../components/piracy/PiracyMap';
import type { PiracyMapRef } from '../../components/piracy/PiracyMap';
import PiracyIndicatorCard from '../../components/piracy/PiracyIndicatorCard';
import {
  getSummary,
  getReports,
  getAreas,
  getOpenTransits,
  closeTransit,
} from '../../api/piracyApi';
import type { PiracySummary, PiracyReport, PiracyArea, PiracyTransit } from '../../api/piracyApi';

// ── DraggablePaper — drag за DialogTitle (модульный уровень!) ──
function DraggablePaper(props: PaperProps) {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.MuiDialogTitle-root')) return;
    if (target.closest('button')) return;
    e.preventDefault();
    setDragging(true);
    setOffset({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e: MouseEvent) => {
      setPos({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    };
    const handleUp = () => setDragging(false);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [dragging, offset]);

  return (
    <Paper
      {...props}
      onMouseDown={handleMouseDown}
      style={{
        ...props.style,
        transform: `translate(${pos.x}px, ${pos.y}px)`,
      }}
      sx={{
        ...((props as any).sx || {}),
        '& .MuiDialogTitle-root': { cursor: dragging ? 'grabbing' : 'grab' },
      }}
    />
  );
}

const AdminPiracyDashboard: React.FC = () => {
  const mapRef = useRef<PiracyMapRef>(null);
  const [summary, setSummary] = useState<PiracySummary | null>(null);
  const [reports, setReports] = useState<PiracyReport[]>([]);
  const [areas, setAreas] = useState<PiracyArea[]>([]);
  const [transits, setTransits] = useState<PiracyTransit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<number | null>(null);
  const [emailModal, setEmailModal] = useState<{
    to: string;
    subject: string;
    body: string;
  } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, r, a, t] = await Promise.all([
        getSummary(),
        getReports(100),
        getAreas(),
        getOpenTransits(),
      ]);
      setSummary(s);
      setReports(r);
      setAreas(a);
      setTransits(t);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCloseTransit = async (transitId: number) => {
    setClosingId(transitId);
    try {
      await closeTransit(transitId);
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Ошибка закрытия транзита');
    } finally {
      setClosingId(null);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  if (loading && !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5">Транзит пирато-опасных районов (HRA Piracy Monitoring)</Typography>
        <Tooltip title="Обновить">
          <IconButton onClick={loadData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Сводка */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <PiracyIndicatorCard summary={summary} loading={loading} />
        </Grid>
        <Grid item xs={12} md={8} id="piracy-map-container">
          <PiracyMap ref={mapRef} areas={areas} reports={reports} />
        </Grid>
      </Grid>

      {/* Открытые транзиты */}
      {transits.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Открытые транзиты ({transits.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Судно</TableCell>
                  <TableCell>Район</TableCell>
                  <TableCell>Сообщений</TableCell>
                  <TableCell>Открыт</TableCell>
                  <TableCell align="right">Действие</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transits.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{t.id}</TableCell>
                    <TableCell
                      onDoubleClick={() => {
                        const report = reports.find(
                          (r) => r.transit_id === t.id && r.latitude != null
                        );
                        if (report) {
                          mapRef.current?.showVesselPopup(report);
                          mapRef.current?.centerOn(report.latitude!, report.longitude!, 8);
                          document
                            .getElementById('piracy-map-container')
                            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }}
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {t.vessel?.vessel_name || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t.area?.code || '—'}
                        color="warning"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{t.report_count}</TableCell>
                    <TableCell>{formatDate(t.opened_at)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        disabled={closingId === t.id}
                        startIcon={
                          closingId === t.id ? <CircularProgress size={14} /> : <CloseIcon />
                        }
                        onClick={() => handleCloseTransit(t.id)}
                      >
                        Закрыть
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Последние сообщения */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Последние сообщения
        </Typography>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Судно</TableCell>
                <TableCell>Район</TableCell>
                <TableCell>Координаты</TableCell>
                <TableCell>Курс / Скорость</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell>Источник</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Нет сообщений
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.id}</TableCell>
                    <TableCell
                      onDoubleClick={() => {
                        if (r.latitude != null && r.longitude != null) {
                          mapRef.current?.showVesselPopup(r);
                          mapRef.current?.centerOn(r.latitude, r.longitude, 8);
                          document
                            .getElementById('piracy-map-container')
                            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }}
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {r.vessel?.vessel_name || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.area?.code || '—'} variant="outlined" />
                    </TableCell>
                    <TableCell
                      onDoubleClick={() => {
                        if (r.latitude != null && r.longitude != null) {
                          mapRef.current?.centerOn(r.latitude, r.longitude, 10);
                          document
                            .getElementById('piracy-map-container')
                            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        fontFamily: 'monospace',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {r.latitude != null && r.longitude != null
                        ? `${r.latitude.toFixed(4)}, ${r.longitude.toFixed(4)}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {r.course != null ? `${r.course}°` : '—'} /{' '}
                      {r.speed != null ? `${r.speed} kn` : '—'}
                    </TableCell>
                    <TableCell>{formatDate(r.created_at)}</TableCell>
                    <TableCell
                      onDoubleClick={() => {
                        setEmailModal({
                          to: 'od_ssto_01@geopallada.ru',
                          subject: r.raw_subject || '(без темы)',
                          body: r.raw_body || '(содержание недоступно)',
                        });
                      }}
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          maxWidth: 150,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.source_email || r.raw_subject || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Email-модалка */}
      {emailModal && (
        <Dialog
          open
          onClose={() => setEmailModal(null)}
          maxWidth="md"
          fullWidth
          PaperComponent={DraggablePaper}
          TransitionComponent={Fade}
        >
          <DialogTitle
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            Содержание сообщения
            <Box>
              <Tooltip title="Копировать всё">
                <IconButton
                  onClick={() => {
                    const text = `Кому: ${emailModal.to}\nТема: ${emailModal.subject}\n\n${emailModal.body}`;
                    navigator.clipboard.writeText(text);
                  }}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setEmailModal(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle2">Кому:</Typography>
            <Typography variant="body2" sx={{ mb: 1, fontFamily: 'monospace', userSelect: 'text' }}>
              {emailModal.to}
            </Typography>
            <Typography variant="subtitle2">Тема:</Typography>
            <Typography variant="body2" sx={{ mb: 1, userSelect: 'text' }}>
              {emailModal.subject}
            </Typography>
            <Typography variant="subtitle2">Содержание:</Typography>
            <Typography
              variant="body2"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', userSelect: 'text', mt: 0.5 }}
            >
              {emailModal.body}
            </Typography>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default AdminPiracyDashboard;
